// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { Loader2, Power, PowerOff, TriangleAlert } from "lucide-react";
import { useRef } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useTogglePiiMarkerMaskActiveMutation } from "@/api/hooks/use-pii-marker";
import { WizardPane } from "@/components/shared/wizard-pane";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

export interface PiiMarkerToggleTarget {
  cameraId: number;
  label: string;
  isActive: boolean;
}

interface PiiMarkerToggleDialogProps {
  target: PiiMarkerToggleTarget | null;
  onClose: () => void;
}

export function PiiMarkerToggleDialog({ target, onClose }: PiiMarkerToggleDialogProps) {
  const { t } = useTranslation("back-office");
  // Keep showing the last target while the dialog animates out
  const last = useRef(target);
  if (target) last.current = target;
  const display = target ?? last.current;
  const toggle = useTogglePiiMarkerMaskActiveMutation();

  const deactivating = !!display?.isActive;
  const Icon = deactivating ? PowerOff : Power;
  const confirmLabel = deactivating ? t("piiMarker.actions.deactivate") : t("piiMarker.actions.activate");

  async function handleConfirm() {
    if (!display) return;
    try {
      await toggle.mutateAsync({ cameraId: display.cameraId, isActive: !display.isActive });
      toast.success(`${confirmLabel}: ${display.label}`);
      onClose();
    } catch {
      // Error toast handled by API middleware
    }
  }

  return (
    <Dialog open={!!target} onOpenChange={() => !toggle.isPending && onClose()}>
      <DialogContent id="dialog-toggle-pii-mask" className="sm:max-w-[33rem] p-0 gap-0 overflow-hidden flex flex-col">
        <div className="flex flex-row">
          <WizardPane variant={deactivating ? "destructive" : "default"} icon={Icon} />
          <div className="flex-1 p-6 flex flex-col gap-4 min-w-0">
            <DialogHeader>
              <DialogTitle>
                {deactivating ? t("piiMarker.actions.deactivateTitle") : t("piiMarker.actions.activateTitle")}
              </DialogTitle>
              <DialogDescription asChild>
                <div>
                  <p>
                    {deactivating ? t("piiMarker.actions.deactivatePrompt") : t("piiMarker.actions.activatePrompt")}
                    <br />
                    <span className="font-semibold font-mono text-foreground">{display?.label}</span>?
                  </p>
                  {deactivating && (
                    <div className="mt-3 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-amber-800 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-200">
                      <TriangleAlert className="h-4 w-4 shrink-0 mt-0.5" />
                      <span className="text-sm">{t("piiMarker.actions.deactivateWarning")}</span>
                    </div>
                  )}
                </div>
              </DialogDescription>
            </DialogHeader>
          </div>
        </div>
        <DialogFooter className="mx-0 mb-0 rounded-none rounded-b-lg">
          <Button variant="outline" onClick={onClose} disabled={toggle.isPending}>
            {t("buttons.cancel")}
          </Button>
          <Button
            variant={deactivating ? "destructive" : "default"}
            className={
              deactivating ? "" : "bg-green-700 text-white hover:bg-green-800 dark:bg-green-700 dark:hover:bg-green-600"
            }
            onClick={handleConfirm}
            disabled={toggle.isPending}
          >
            {toggle.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Icon className="h-4 w-4" />}
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
